/**
 * AI CONTEXT
 * Purpose: derives exception data structures (assistants/makeups/tryouts/planned absences) per selected session.
 * Outputs: studentsByExceptionType, exceptionDataBySessionId, attendance summaries, id maps.
 * Depends on: selected sessions + makeup/tryout/student/user joins.
 * Used by: AdminViewContextProvider and attendance/exception UI.
 * Refactor: dedupe repeated per-session/per-global exception-building logic.
 */
import { keyBy, orderBy, uniq } from "lodash"
import { useMemo } from "react"

import { makeupAtoms, tryoutAtoms } from "#atoms"
import { ROOMS } from "#constants/classOptions"
import { EXCEPTION_LIST_VIEWS } from "#constants/exceptionListConstants"
import { DOC_TYPES } from "#customTypes/composedTypes/firebaseTypes"
import { MakeupConstructor } from "#customTypes/composedTypes/makeupTypes"
import { StudentConstructor } from "#customTypes/composedTypes/studentTypes"
import { TryoutConstructor } from "#customTypes/composedTypes/tryoutTypes"
import { Uuid } from "#customTypes/globalTypes"
import {
  useIsAllFulfilled,
  useIsFulfilled,
} from "#hooks/jotai/useCascadingData"
import { useGetSomeStudents } from "#hooks/jotai/useGetStudents"
import { useSomeDocs, useStableIds } from "#hooks/jotai/useSubscription"
import {
  getAllSessionMemberIds,
  getSessionAttendanceSummary,
  getSessionInfoFromId,
} from "#utils/classHelpers"

import { AdminViewClassSession } from "./useAdminViewData"
import { useGetSomeUsers } from "./useGetUser"
export interface ExceptionStudentData {
  id: Uuid
  sessionId: Uuid
  classId: Uuid
  sessionIndex: number
  room: ROOMS
  student: StudentConstructor
  exceptionType: "assistant" | "makeup" | "tryout" | "plannedAbsence"
  exceptionData?: MakeupConstructor | TryoutConstructor
}

export interface StudentsByExceptionType {
  [EXCEPTION_LIST_VIEWS.assistants]: ExceptionStudentData[]
  [EXCEPTION_LIST_VIEWS.makeups]: ExceptionStudentData[]
  [EXCEPTION_LIST_VIEWS.tryouts]: ExceptionStudentData[]
  [EXCEPTION_LIST_VIEWS.plannedAbsences]: ExceptionStudentData[]
  [EXCEPTION_LIST_VIEWS.all]: ExceptionStudentData[]
}

export interface ExceptionDataBySessionId {
  [sessionId: string]: StudentsByExceptionType
}

const useExceptionListData = (selectedSessions: AdminViewClassSession[]) => {
  const allSessionMemberIds = useMemo(() => {
    return getAllSessionMemberIds(
      // Convert AdminViewClassSession into ClassSessionConstructor
      selectedSessions?.map(({ session }) => session)
    )
  }, [selectedSessions])

  const stableMakeupIds = useStableIds([
    ...allSessionMemberIds.makeupIds,
    ...allSessionMemberIds.plannedAbsenceIds,
  ])
  const stableTryoutIds = useStableIds(allSessionMemberIds.tryoutIds)
  const stableTeacherIds = useStableIds(allSessionMemberIds.teacherIds)
  const stableStudentIds = useStableIds(allSessionMemberIds.studentIds)
  const stableAssistantIds = useStableIds(allSessionMemberIds.assistantIds)

  const makeups = useSomeDocs<MakeupConstructor>(
    makeupAtoms,
    DOC_TYPES.MAKEUPS,
    stableMakeupIds
  )

  const tryouts = useSomeDocs<TryoutConstructor>(
    tryoutAtoms,
    DOC_TYPES.TRYOUTS,
    stableTryoutIds
  )

  const teachers = useGetSomeUsers(stableTeacherIds)

  const allStudentIds = useMemo(() => {
    return uniq([
      ...stableStudentIds,
      ...stableAssistantIds,
      ...(makeups?.map(({ studentId }) => studentId) ?? []),
      ...(tryouts?.map(({ studentId }) => studentId) ?? []),
    ]).sort()
  }, [stableStudentIds, stableAssistantIds, makeups, tryouts])

  const stableAllStudentIds = useStableIds(allStudentIds)
  const students = useGetSomeStudents(stableAllStudentIds)

  // Track fulfillment
  const makeupsFulfilled = useIsFulfilled(stableMakeupIds, makeups, 3000)
  const tryoutsFulfilled = useIsFulfilled(stableTryoutIds, tryouts, 3000)

  const isReady = useIsAllFulfilled([makeupsFulfilled, tryoutsFulfilled])

  const teachersById = useMemo(() => {
    return keyBy(teachers, "id")
  }, [teachers])

  const studentsById = useMemo(() => {
    return keyBy(students, "id")
  }, [students])

  const makeupsById = useMemo(() => {
    return keyBy(makeups, "id")
  }, [makeups])

  const tryoutsById = useMemo(() => {
    return keyBy(tryouts, "id")
  }, [tryouts])

  const studentsByExceptionType: StudentsByExceptionType = useMemo(() => {
    if (!selectedSessions?.length || !isReady) {
      return {
        [EXCEPTION_LIST_VIEWS.assistants]: [],
        [EXCEPTION_LIST_VIEWS.makeups]: [],
        [EXCEPTION_LIST_VIEWS.tryouts]: [],
        [EXCEPTION_LIST_VIEWS.plannedAbsences]: [],
        [EXCEPTION_LIST_VIEWS.all]: [],
      }
    }

    const allAssistants: ExceptionStudentData[] = []
    const allMakeups: ExceptionStudentData[] = []
    const allTryouts: ExceptionStudentData[] = []
    const allPlannedAbsences: ExceptionStudentData[] = []

    // Iterate through each session to create exceptions with sessionId
    selectedSessions.forEach(({ session, room }) => {
      const [classId, sessionIndex] = getSessionInfoFromId(session.id)
      // Assistants for this session
      session.assistantIds?.forEach((studentId) => {
        const student = studentsById[studentId]
        if (student) {
          allAssistants.push({
            id: `${studentId}-assistant-${session.id}`,
            sessionId: session.id,
            classId,
            sessionIndex,
            room,
            student,
            exceptionType: "assistant",
          })
        }
      })

      // Makeups for this session
      session.makeupIds?.forEach((makeupId) => {
        const makeup = makeupsById[makeupId]
        if (makeup) {
          const student = studentsById[makeup.studentId]
          if (student) {
            allMakeups.push({
              id: `${makeup.studentId}-makeup-${makeupId}`,
              sessionId: session.id,
              classId,
              sessionIndex,
              room,
              student,
              exceptionType: "makeup",
              exceptionData: makeup,
            })
          }
        }
      })

      // Tryouts for this session
      session.tryoutIds?.forEach((tryoutId) => {
        const tryout = tryoutsById[tryoutId]
        if (tryout) {
          const student = studentsById[tryout.studentId]
          if (student) {
            allTryouts.push({
              id: `${tryout.studentId}-tryout-${tryoutId}`,
              sessionId: session.id,
              classId,
              sessionIndex,
              room,
              student,
              exceptionType: "tryout",
              exceptionData: tryout,
            })
          }
        }
      })

      // Planned absences for this session
      session.plannedAbsenceIds?.forEach((plannedAbsenceId) => {
        const makeup = makeupsById[plannedAbsenceId]
        if (makeup) {
          const student = studentsById[makeup.studentId]
          if (student) {
            allPlannedAbsences.push({
              id: `${makeup.studentId}-plannedAbsence-${plannedAbsenceId}`,
              sessionId: session.id,
              classId,
              sessionIndex,
              room,
              student,
              exceptionType: "plannedAbsence",
              exceptionData: makeup,
            })
          }
        }
      })
    })

    // Sort each exception type by student name
    const sortedAssistants = orderBy(allAssistants, "student.name", "asc")
    const sortedMakeupStudents = orderBy(allMakeups, "student.name", "asc")
    const sortedTryoutStudents = orderBy(allTryouts, "student.name", "asc")
    const sortedPlannedAbsences = orderBy(
      allPlannedAbsences,
      "student.name",
      "asc"
    )

    const allExceptions = [
      ...sortedAssistants,
      ...sortedMakeupStudents,
      ...sortedTryoutStudents,
      ...sortedPlannedAbsences,
    ]
    const sortedAllExceptions = orderBy(allExceptions, "student.name", "asc")

    return {
      [EXCEPTION_LIST_VIEWS.assistants]: sortedAssistants,
      [EXCEPTION_LIST_VIEWS.makeups]: sortedMakeupStudents,
      [EXCEPTION_LIST_VIEWS.tryouts]: sortedTryoutStudents,
      [EXCEPTION_LIST_VIEWS.plannedAbsences]: sortedPlannedAbsences,
      [EXCEPTION_LIST_VIEWS.all]: sortedAllExceptions,
    }
  }, [selectedSessions, studentsById, makeupsById, tryoutsById, isReady])

  const exceptionDataBySessionId: ExceptionDataBySessionId = useMemo(() => {
    if (!selectedSessions?.length || !isReady) {
      return {}
    }

    const sessionData: ExceptionDataBySessionId = {}

    selectedSessions.forEach(({ session, room }) => {
      const [classId, sessionIndex] = getSessionInfoFromId(session.id)

      const sessionAssistants: ExceptionStudentData[] = []
      const sessionMakeups: ExceptionStudentData[] = []
      const sessionTryouts: ExceptionStudentData[] = []
      const sessionPlannedAbsences: ExceptionStudentData[] = []

      // Assistants for this session
      session.assistantIds?.forEach((studentId) => {
        const student = studentsById[studentId]
        if (student) {
          sessionAssistants.push({
            id: `${studentId}-assistant-${session.id}`,
            sessionId: session.id,
            classId,
            sessionIndex,
            room,
            student,
            exceptionType: "assistant",
          })
        }
      })

      // Makeups for this session
      session.makeupIds?.forEach((makeupId) => {
        const makeup = makeupsById[makeupId]
        if (makeup) {
          const student = studentsById[makeup.studentId]
          if (student) {
            sessionMakeups.push({
              id: `${makeup.studentId}-makeup-${makeupId}`,
              sessionId: session.id,
              classId,
              sessionIndex,
              room,
              student,
              exceptionType: "makeup",
              exceptionData: makeup,
            })
          }
        }
      })

      // Tryouts for this session
      session.tryoutIds?.forEach((tryoutId) => {
        const tryout = tryoutsById[tryoutId]
        if (tryout) {
          const student = studentsById[tryout.studentId]
          if (student) {
            sessionTryouts.push({
              id: `${tryout.studentId}-tryout-${tryoutId}`,
              sessionId: session.id,
              classId,
              sessionIndex,
              room,
              student,
              exceptionType: "tryout",
              exceptionData: tryout,
            })
          }
        }
      })

      // Planned absences for this session
      session.plannedAbsenceIds?.forEach((plannedAbsenceId) => {
        const makeup = makeupsById[plannedAbsenceId]
        if (makeup) {
          const student = studentsById[makeup.studentId]
          if (student) {
            sessionPlannedAbsences.push({
              id: `${makeup.studentId}-plannedAbsence-${plannedAbsenceId}`,
              sessionId: session.id,
              classId,
              sessionIndex,
              room,
              student,
              exceptionType: "plannedAbsence",
              exceptionData: makeup,
            })
          }
        }
      })

      // Sort each exception type by student name for this session
      const sortedSessionAssistants = orderBy(
        sessionAssistants,
        "student.name",
        "asc"
      )

      const sortedSessionMakeups = orderBy(
        sessionMakeups,
        "student.name",
        "asc"
      )

      const sortedSessionTryouts = orderBy(
        sessionTryouts,
        "student.name",
        "asc"
      )

      const sortedSessionPlannedAbsences = orderBy(
        sessionPlannedAbsences,
        "student.name",
        "asc"
      )

      const allSessionExceptions = [
        ...sortedSessionAssistants,
        ...sortedSessionMakeups,
        ...sortedSessionTryouts,
        ...sortedSessionPlannedAbsences,
      ]
      const sortedAllSessionExceptions = orderBy(
        allSessionExceptions,
        "student.name",
        "asc"
      )

      sessionData[session.id] = {
        [EXCEPTION_LIST_VIEWS.assistants]: sortedSessionAssistants,
        [EXCEPTION_LIST_VIEWS.makeups]: sortedSessionMakeups,
        [EXCEPTION_LIST_VIEWS.tryouts]: sortedSessionTryouts,
        [EXCEPTION_LIST_VIEWS.plannedAbsences]: sortedSessionPlannedAbsences,
        [EXCEPTION_LIST_VIEWS.all]: sortedAllSessionExceptions,
      }
    })

    return sessionData
  }, [selectedSessions, studentsById, makeupsById, tryoutsById, isReady])

  const attendanceSummariesBySessionId = useMemo(() => {
    const sessionSummaries = selectedSessions?.map(({ session }) => {
      const summary = getSessionAttendanceSummary(
        session,
        studentsById,
        studentsByExceptionType
      )

      return { summary, sessionId: session.id }
    })

    return keyBy(sessionSummaries, "sessionId")
  }, [selectedSessions, studentsById, studentsByExceptionType])

  return {
    studentsByExceptionType,
    exceptionDataBySessionId,
    teachersById,
    studentsById,
    attendanceSummariesBySessionId,
    isReady,
  }
}

export default useExceptionListData
